import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  Button,
} from "@mui/material";
import WorkIcon from "@mui/icons-material/WorkOutlineOutlined";
import PeopleIcon from "@mui/icons-material/PersonOutlineOutlined";
import PendingActionsIcon from "@mui/icons-material/PendingActionsOutlined";
import ScheduleIcon from "@mui/icons-material/ScheduleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { UserApi } from "../../../apis";
import { can } from "../../../utils/auth";
import {
  PageHeader,
  StatCard,
  DateRangeFilter,
  CUSTOM_RANGE,
  formatRange,
} from "../../../components/ui";
import {
  ChartCard,
  HoursTrendChart,
  HoursByProjectChart,
  StatusBreakdown,
} from "../../../components/charts";
import { useTimeAnalytics } from "../../../hooks/useTimeAnalytics";

/** Compact leaderboard — a table, not a chart: the names are the point. */
function TopList({ items, unit = "h", emptyText }) {
  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  const max = items[0].hours || 1;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
      {items.map((item) => (
        <Box key={item.name}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
            <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {item.name}
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}
            >
              {item.hours.toFixed(1)} {unit}
            </Typography>
          </Box>
          {/* Same-ramp track: a meter, not a second colour encoding. */}
          <Box sx={{ height: 5, borderRadius: 999, bgcolor: "background.subtle" }}>
            <Box
              sx={{
                width: `${(item.hours / max) * 100}%`,
                height: "100%",
                borderRadius: 999,
                bgcolor: "primary.main",
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [range, setRange] = useState(30);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [counts, setCounts] = useState(null);
  const [countsLoading, setCountsLoading] = useState(true);

  const isCustom = range === CUSTOM_RANGE;
  const a = useTimeAnalytics({
    days: isCustom ? 30 : range,
    // Ignored until both ends are filled in, so the charts don't blank out
    // while the first date is being picked.
    from: isCustom && from && to ? from : null,
    to: isCustom && from && to ? to : null,
  });

  // The counts aggregate every user in the system, so the endpoint requires
  // users.view. A role that lands in the admin area without it — one scoped
  // to invoicing, say — would otherwise fire a request that can only 403.
  const canSeeCounts = can("users.view");

  useEffect(() => {
    if (!canSeeCounts) {
      setCountsLoading(false);
      return undefined;
    }

    let cancelled = false;
    UserApi.getAdminDashboardData()
      .then((res) => {
        if (!cancelled) setCounts(res.data.data);
      })
      .catch((err) => {
        console.error("Failed to load dashboard counts:", err);
        if (!cancelled) setCounts(null);
      })
      .finally(() => {
        if (!cancelled) setCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canSeeCounts]);

  // Always name the actual span, so "30d" isn't the only clue to what the
  // numbers cover.
  const dateSpan = formatRange(a.startDate, a.endDate);
  // The one number an admin acts on: submitted time still waiting on them.
  const pendingHours =
    a.byStatus.find((s) => String(s.status).toLowerCase() === "pending")?.hours || 0;

  const rangePicker = (
    <DateRangeFilter
      value={range}
      onChange={setRange}
      from={from}
      to={to}
      onFromChange={setFrom}
      onToChange={setTo}
    />
  );

  return (
    <Box>
      <PageHeader
        title="Dashboard"
        subtitle={dateSpan}
        actions={rangePicker}
      />

      {/* KPI row — headline numbers, not a chart. */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Hours logged"
            value={a.loading ? "—" : a.totalHours.toFixed(1)}
            unit="h"
            icon={ScheduleIcon}
            color="primary"
            loading={a.loading}
          />
        </Grid>
        {/* Both counts come from the users.view endpoint, so they are left
            out rather than shown as zero for a role that cannot read it. */}
        {canSeeCounts && (
        <Grid item xs={6} md={3}>
          <StatCard
            label="Projects"
            value={countsLoading ? "—" : counts?.projects ?? 0}
            icon={WorkIcon}
            color="violet"
            loading={countsLoading}
          />
        </Grid>
        )}
        {canSeeCounts && (
        <Grid item xs={6} md={3}>
          <StatCard
            label="Team members"
            value={countsLoading ? "—" : counts?.users ?? 0}
            icon={PeopleIcon}
            color="teal"
            loading={countsLoading}
          />
        </Grid>
        )}
        <Grid item xs={6} md={3}>
          <StatCard
            label="Awaiting approval"
            value={a.loading ? "—" : pendingHours.toFixed(1)}
            unit="h"
            icon={PendingActionsIcon}
            color="warning"
            loading={a.loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Hours logged over time"
            loading={a.loading}
            isEmpty={!a.loading && a.totalHours === 0}
            height={280}
          >
            <HoursTrendChart data={a.byDay} height={280} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Timesheet status"
            loading={a.loading}
            isEmpty={!a.loading && a.byStatus.length === 0}
            height={200}
          >
            <StatusBreakdown data={a.byStatus} total={a.totalHours} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={7}>
          <ChartCard
            title="Hours by project"
            loading={a.loading}
            isEmpty={!a.loading && a.byProject.length === 0}
            height={280}
            action={
              <Button
                size="small"
                endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
                onClick={() => navigate("/admin/time-report")}
              >
                Report
              </Button>
            }
          >
            <HoursByProjectChart data={a.byProject} height={280} />
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card sx={{ p: { xs: 2, sm: 2.5 }, height: "100%" }}>
            <Typography variant="h6">Most active people</Typography>
            <Box sx={{ mt: 2 }}>
              <TopList
                items={a.byUser.slice(0, 5)}
                emptyText="No time logged in this period."
              />
            </Box>

          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
